import { HelpArticle, SearchIndexItem } from "@/types/help.types.ts";
import {
  Avatar,
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Text,
  titleTheme,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface SearchResult extends SearchIndexItem {
  score: number;
  matchType: "title" | "description" | "content";
}

interface HelpSearchResultsProps {
  results: SearchResult[];
  query: string;
  isSearching: boolean;
  articles: HelpArticle[];
}

const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;

  const terms = query.toLowerCase().trim().split(/\s+/);

  const pattern = new RegExp(
    `(${terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi"
  );

  const parts = text.split(pattern);

  return parts.map((part, index) => {
    const isMatch = terms.some((term) => part.toLowerCase() === term.toLowerCase());

    if (isMatch) {
      return (
        <mark key={index} className="bg-brand-dark/20 text-dark rounded-sm px-1">
          {part}
        </mark>
      );
    }
    return part;
  });
};

export const HelpSearchResults = ({
  results,
  query,
  isSearching,
  articles,
}: HelpSearchResultsProps) => {
  // const { t } = useTranslation();

  const highlightedArticles = useMemo(() => {
    return results
      .map((result) => {
        const article = articles.find((a) => a.id === result.id);
        if (!article) return null;

        return {
          ...article,
          titleNode: highlightText(article.title, query),
          descriptionNode: highlightText(article.description, query),
        };
      })
      .filter(
        (
          article
        ): article is HelpArticle & {
          titleNode: React.ReactNode;
          descriptionNode: React.ReactNode;
        } => article !== null
      );
  }, [results, articles, query]);

  if (isSearching) {
    return (
      <Stack gap={"md"}>
        <Skeleton className={"w-full h-20"} />
        <Skeleton className={"w-full h-20"} />
        <Skeleton className={"w-full h-20"} />
      </Stack>
    );
  }

  // if (results.length === 0) {
  //   return (
  //     <Box variant={"darkRounded"} className={"text-center py-12"}>
  //       <Stack items={"center"} gap={"md"}>
  //         <FontAwesomeIcon icon={"fa-light fa-search"} className={"text-6xl text-secondary"} />
  //         <Title
  //           header={t("common:no_results_found")}
  //           caption={t("common:try_different_search_terms")}
  //         />
  //       </Stack>
  //     </Box>
  //   );
  // }

  return (
    <Box variant={"darkRounded"}>
      <Stack gap={"sm"}>
        {/*<Box className={"px-4.5 py-3"}>*/}
        {/*  <Title*/}
        {/*    caption={t("plurals:search_results_count", {*/}
        {/*      count: results.length,*/}
        {/*      query,*/}
        {/*    })}*/}
        {/*  />*/}
        {/*</Box>*/}

        {highlightedArticles.map((article) => (
          <Link to={`/help/article/${article.id}`} key={`result-article-${article.id}`}>
            <Stack
              direction={"horizontal"}
              justify={"between"}
              items={"start"}
              gap={"xl"}
              className={twMerge(
                "w-full bg-light rounded-lg p-4.5 hover:bg-dark/5 cursor-pointer h-full"
              )}
            >
              <Stack
                className={"flex flex-1 w-full"}
                direction={"horizontal"}
                items={"start"}
                gap={"md"}
              >
                {article.icon && (
                  <Avatar
                    size={"xl"}
                    icon={
                      <FontAwesomeIcon
                        className={"text-secondary"}
                        icon={`fa-light ${article.icon}`}
                      />
                    }
                  />
                )}
                <Stack>
                  <h3 className={twMerge(titleTheme.variants.size.md, "whitespace-pre-wrap")}>
                    {article.titleNode}
                  </h3>

                  <Text className={twMerge(titleTheme.variants.caption.md, "whitespace-pre-wrap")}>
                    {article.descriptionNode}
                  </Text>
                </Stack>
              </Stack>

              <ButtonIcon
                variant={"secondary"}
                icon={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
              />
            </Stack>
          </Link>
        ))}
      </Stack>
    </Box>
  );
};
